/**
 * O que o resultado do LeadExplorer mostra: abas que têm dados, na ordem da proposta, com as
 * contagens (redes, tecnologias) e as abas que não se aplicam a um site (sem CNPJ, fora do .com.br).
 */
import type { LxKey } from './leText';
import { parseReport, reportToText } from './report';
import type {
  ExplorerData,
  ExplorerLinkedin,
  ExplorerBritishRegistry,
  ExplorerFrenchRegistry,
  ExplorerMode,
  ExplorerStackTechnology,
  ExplorerTechnology,
  ExplorerWhoisEntity,
} from './types';
import type { HistoryEntry } from './historyClient';

export type TabId =
  | 'contacts'
  | 'strategy'
  | 'rf'
  | 'group'
  | 'fr'
  | 'gb'
  | 'linkedin'
  | 'domain'
  | 'social'
  | 'site'
  | 'tech';

export const TAB_ORDER: TabId[] = [
  'contacts',
  'strategy',
  'rf',
  'group',
  'fr',
  'gb',
  'linkedin',
  'domain',
  'social',
  'site',
  'tech',
];

export const TAB_ICON: Record<TabId, string> = {
  contacts: 'contacts',
  strategy: 'tips_and_updates',
  rf: 'account_balance',
  group: 'account_tree',
  fr: 'account_balance',
  gb: 'account_balance',
  linkedin: 'work',
  domain: 'dns',
  social: 'share',
  site: 'web',
  tech: 'code',
};

export const RESUME_KEYS = [
  'market_segmentation',
  'products_and_services',
  'company_culture',
  'mission',
  'vision',
  'values',
  'target_market',
  'number_of_employees',
  'annual_revenue',
  'recent_news',
] as const;

export function socialLinks(data: ExplorerData): [string, string][] {
  return Object.entries(data.site?.social_network ?? {}).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0,
  );
}

export function technologies(data: ExplorerData): [string, ExplorerTechnology[]][] {
  const pick = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const own = pick(data.site?.technologies);
  const source = Object.keys(own).length > 0 ? own : pick(data.site?.stats?.technologies);
  return Object.entries(source)
    .map(([group, list]): [string, ExplorerTechnology[]] => [
      group,
      asArray<ExplorerTechnology>(list).filter((tech) =>
        Boolean(asText(tech.st_name) ?? asText(tech.name)),
      ),
    ])
    .filter(([, list]) => list.length > 0);
}

/** Categoria "Miscellaneous" (Outros) das assinaturas: o grupo vai para o fim. */
const MISC_CATEGORY_ID = '19';

/**
 * Raio-X (decisões 427 a 433): as tecnologias por categoria, como no relatório da Sentinela (uma
 * tecnologia aparece em cada categoria dela). `name` fica em inglês; `label` é o nome que a tela
 * mostra (`techGroup`), e a ordem segue ele, com "Outros" no fim. Sem `stack`, a lista antiga do
 * WhatRuns (`technologies`).
 */
export function stackGroups(
  data: ExplorerData,
  label: (name: string) => string = (name) => name,
): {
  id: string;
  name: string;
  label: string;
  priority: number;
  items: ExplorerStackTechnology[];
}[] {
  const stack = data.site?.stack;
  const list = asArray<ExplorerStackTechnology>(stack?.technologies).filter((tech) =>
    Boolean(asText(tech?.name)),
  );
  const names = stack?.categories ?? {};
  const groups = new Map<
    string,
    { id: string; name: string; priority: number; items: ExplorerStackTechnology[] }
  >();
  for (const tech of list) {
    const ids = asArray<number>(tech.categories).length
      ? asArray<number>(tech.categories)
      : [Number(MISC_CATEGORY_ID)];
    for (const raw of ids) {
      const id = String(raw);
      const category = names[id];
      if (!groups.has(id)) {
        groups.set(id, {
          id,
          name: category?.name ?? 'Miscellaneous',
          priority: category?.priority ?? 0,
          items: [],
        });
      }
      groups.get(id)!.items.push(tech);
    }
  }
  const other = (id: string): number => (id === MISC_CATEGORY_ID ? 1 : 0);
  return [...groups.values()]
    .map((group) => ({ ...group, label: label(group.name) }))
    .sort((a, b) => other(a.id) - other(b.id) || a.label.localeCompare(b.label));
}

/** Tem o Raio-X novo (a aba usa ele no lugar do WhatRuns). */
export function hasStack(data: ExplorerData): boolean {
  return asArray(data.site?.stack?.technologies).length > 0;
}

export function techCount(data: ExplorerData): number {
  if (hasStack(data)) return asArray(data.site?.stack?.technologies).length;
  return technologies(data).reduce((sum, [, list]) => sum + list.length, 0);
}

/** Tem registros de DNS (a aba Domínio aparece mesmo sem o registro.br). */
export function hasDns(data: ExplorerData): boolean {
  return asArray(data.site?.dns?.records).length > 0;
}

export function hasSiteTab(data: ExplorerData): boolean {
  const site = data.site;
  if (!site) return false;
  const a = site.analytics;
  return Boolean(
    site.screenshot ||
    site.description ||
    site.meta?.title ||
    site.meta?.description ||
    site.meta?.image ||
    site.category ||
    (site.stats?.chart?.subdomains?.length ?? 0) > 0 ||
    (site.stats?.chart?.keywords?.length ?? 0) > 0 ||
    (a && (a.engagement || a.traffic || a.visits || a.countries?.length || a.adwords?.length)),
  );
}

export function availableTabs(data: ExplorerData, mode?: ExplorerMode): TabId[] {
  const cnpjDigits = String(data.company?.cnpj ?? '').replace(/\D/g, '');
  const has: Record<TabId, boolean> = {
    // Sempre: mesmo sem nenhum contato, a guia diz onde procuramos e o que dá para fazer.
    contacts: true,
    // Sempre: com o dossiê, ele; sem, o botão "Gerar dossiê com IA" (decisões 369 a 378). A aba
    // "Dados públicos" saiu: a pesquisa entrou no dossiê (373).
    strategy: true,
    rf: Boolean(data.company && (data.company.name || data.company.nickname)),
    // Grupo Econômico (305 a 316): empresa brasileira com o cadastro da Receita. Na degustação
    // pelo site, o CNPJ vem embaralhado e não há como montar o grupo.
    group:
      Boolean(data.company && (data.company.name || data.company.nickname)) &&
      cnpjDigits.length === 14 &&
      !(data.masked && mode === 'site'),
    fr: Boolean(frenchRegistry(data)),
    gb: Boolean(britishRegistry(data)),
    linkedin: Boolean(asText(data.linkedin?.url)),
    domain: Boolean(data.site?.whois && Object.keys(data.site.whois).length > 0) || hasDns(data),
    social: socialLinks(data).length > 0,
    site: hasSiteTab(data),
    tech: techCount(data) > 0,
  };
  return TAB_ORDER.filter((tab) => has[tab]);
}

/**
 * Domínio .com.br registrado no CPF de uma pessoa: o registro.br não tem CNPJ para levar à
 * Receita (o titular aparece como `registrant` com `ids.type` "cpf", mascarado).
 */
export function registeredToPerson(data: ExplorerData): boolean {
  return asArray<ExplorerWhoisEntity>(data.site?.whois?.entities).some(
    (entity) =>
      entity?.role === 'registrant' && String(entity.ids?.type ?? '').toLowerCase() === 'cpf',
  );
}

/**
 * A análise aberta como linha do histórico, quando a linha ainda não chegou (a análise acabou de
 * rodar): o resumo sai dos próprios dados, para o "Baixar" montar o arquivo.
 */
export function openEntry(
  data: ExplorerData,
  mode: ExplorerMode,
  term: string,
  name: string,
  contacts: { kind: string }[],
): HistoryEntry {
  const company = data.company;
  return {
    id: '',
    mode,
    key: term,
    status: 'ok',
    summary: {
      name,
      cnpj: asText(data.cnpj) ?? asText(company?.cnpj) ?? undefined,
      domain: asText(data.domain) ?? (mode === 'site' ? term : undefined),
      activity: asText(company?.cnaeDescription) ?? undefined,
      city: asText(company?.address?.city) ?? undefined,
      state: asText(company?.address?.state) ?? undefined,
      situation: asText(company?.status) ?? undefined,
      phones: contacts.filter((c) => c.kind === 'mobile' || c.kind === 'landline').length,
      emails: contacts.filter((c) => c.kind === 'generic' || c.kind === 'personal').length,
      socials: Object.keys(data.site?.social_network ?? {}).length,
    },
    analyzedAt: new Date().toISOString(),
  };
}

/** Site analisado: abas que não se aplicam e por quê (aparecem no quadro da esquerda). */
export function notApplicable(
  data: ExplorerData,
  mode: ExplorerMode,
  term: string,
): ('rf' | 'domain')[] {
  if (mode !== 'site') return [];
  const out: ('rf' | 'domain')[] = [];
  // Empresa francesa ou britânica: a aba do registro do país faz as vezes da Receita.
  if (!data.company && !frenchRegistry(data) && !britishRegistry(data)) out.push('rf');
  if (!term.includes('.com.br') && !data.site?.whois && !hasDns(data)) out.push('domain');
  return out;
}

/** Empresa francesa do resultado (SIREN escrito no site), ou null. */
export function frenchRegistry(data: ExplorerData): ExplorerFrenchRegistry | null {
  const r = data.registry;
  return r && r.country === 'FR' && /^\d{9}$/.test(String(r.siren ?? '')) ? r : null;
}

/** Empresa britânica do resultado (número da Companies House escrito no site), ou null. */
export function britishRegistry(data: ExplorerData): ExplorerBritishRegistry | null {
  const r = data.registry;
  return r && r.country === 'GB' && /^[A-Z0-9]{8}$/.test(String(r.number ?? '')) ? r : null;
}

const GB_STATUS: Record<string, LxKey> = {
  active: 'gb_st_active',
  open: 'gb_st_active',
  registered: 'gb_st_active',
  dissolved: 'gb_st_dissolved',
  liquidation: 'gb_st_liquidation',
  administration: 'gb_st_administration',
  receivership: 'gb_st_receivership',
  'voluntary-arrangement': 'gb_st_arrangement',
  'insolvency-proceedings': 'gb_st_insolvency',
  'converted-closed': 'gb_st_closed',
  closed: 'gb_st_closed',
  removed: 'gb_st_closed',
};

/** Situação na Companies House no idioma da pessoa; situação desconhecida, em inglês. */
export function britishStatus(r: ExplorerBritishRegistry, tx: Tx): string {
  const key = r.statusCode ? GB_STATUS[r.statusCode] : undefined;
  return key ? tx(key) : (r.statusLabel ?? r.statusCode ?? '');
}

const GB_SIZE: Record<string, [LxKey, LxKey]> = {
  micro: ['gb_size_micro', 'gb_size_micro_rev'],
  small: ['gb_size_small', 'gb_size_small_rev'],
  medium: ['gb_size_medium', 'gb_size_medium_rev'],
  dormant: ['gb_size_dormant', 'gb_size_dormant_rev'],
};

/** Porte pelo tipo do último balanço: nome curto e o que ele quer dizer. */
export function britishSize(size: string | undefined): [LxKey, LxKey] | null {
  return size ? (GB_SIZE[size] ?? null) : null;
}

/** "75-100" → "75% ou mais"; "25-50" → "25% a 50%". */
function shareBand(band: string, tx: Tx): string {
  const [from, to] = band.split('-');
  return to === '100'
    ? tx('gb_band_plus', { from: from ?? '' })
    : tx('gb_band_range', { from: from ?? '', to: to ?? '' });
}

/** O controle de quem está na PSC, em frases curtas: "Ações: 75% ou mais", "Nomeia diretores". */
export function britishControl(
  control: NonNullable<ExplorerBritishRegistry['owners']>[number]['control'],
  tx: Tx,
): string[] {
  if (!control) return [];
  return [
    control.shares ? tx('gb_ctrl_shares', { band: shareBand(control.shares, tx) }) : '',
    control.votes ? tx('gb_ctrl_votes', { band: shareBand(control.votes, tx) }) : '',
    control.surplus ? tx('gb_ctrl_surplus', { band: shareBand(control.surplus, tx) }) : '',
    control.directors ? tx('gb_ctrl_directors') : '',
    control.influence ? tx('gb_ctrl_influence') : '',
  ].filter(Boolean);
}

/** 552032534 → 552 032 534; SIRET 55203253400703 → 552 032 534 00703. */
export function sirenMask(value: string | undefined): string {
  const d = String(value ?? '').replace(/\D/g, '');
  if (d.length === 9) return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
  if (d.length === 14) return `${sirenMask(d.slice(0, 9))} ${d.slice(9)}`;
  return d;
}

const FR_CATEGORY: Record<string, [LxKey, LxKey]> = {
  PME: ['fr_cat_pme', 'fr_cat_pme_rev'],
  ETI: ['fr_cat_eti', 'fr_cat_eti_rev'],
  GE: ['fr_cat_ge', 'fr_cat_ge_rev'],
};

/** Categoria do INSEE (PME, ETI, GE) → nome e o que ela quer dizer. */
export function frenchCategory(category: string | undefined): [LxKey, LxKey] | null {
  return category ? (FR_CATEGORY[category.toUpperCase()] ?? null) : null;
}

type Tx = (key: LxKey, vars?: Record<string, string | number>) => string;

/** Faixa de funcionários do registro francês: "1.000 a 1.999", "10.000 ou mais", "Nenhum". */
export function frenchEmployees(
  e: ExplorerFrenchRegistry['employees'],
  tx: Tx,
  locale: string,
): string | null {
  if (!e) return null;
  const n = (value: number): string => value.toLocaleString(locale);
  if (e.max === 0) return tx('fr_employees_none');
  if (e.max == null) return tx('fr_employees_plus', { n: n(e.min) });
  return tx('fr_employees_range', { from: n(e.min), to: n(e.max) });
}

/**
 * Valor em euros; de 1 milhão para cima, abreviado ("27,4 bi €"). No PDF vai "EUR": o símbolo
 * do euro fica fora das fontes padrão dele.
 */
export function euros(value: number, locale: string, code = false): string {
  const big = Math.abs(value) >= 1_000_000;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    currencyDisplay: code ? 'code' : 'symbol',
    maximumFractionDigits: big ? 1 : 0,
    notation: big ? 'compact' : 'standard',
  }).format(value);
}

/** "Pão Dourado" → "PD". */
export function initials(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 1 || /\d/.test(w));
  const letters = (words.length > 1 ? [words[0], words[1]] : [words[0] ?? name]).map((w) =>
    (w ?? '').charAt(0).toUpperCase(),
  );
  return letters.join('').slice(0, 2) || '?';
}

/**
 * "PADARIA PÃO DOURADO LTDA" → "Padaria Pão Dourado Ltda": preposições em minúsculas e as
 * siglas societárias na forma certa ("S.A.", "S/A", "Ltda", "ME", "EPP", "EIRELI").
 */
export function titleCase(value: string): string {
  return value
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/(^|[\s/(-])(\p{L})/gu, (_m, sp: string, ch: string) => sp + ch.toUpperCase())
    .replace(/(?<=\s)(De|Da|Do|Das|Dos|E|Em|Para|Com|A|O|As|Os)(?=\s)/g, (w) => w.toLowerCase())
    .replace(/\bS\.\s?a\.?(?=\s|$)/gi, 'S.A.')
    .replace(/\bS\/a\b/gi, 'S/A')
    .replace(/\bLtda\b\.?/gi, 'Ltda')
    .replace(/\s(Me|Epp|Eireli)\.?$/i, (_m, w: string) => ` ${w.toUpperCase()}`);
}

/** Só passa para maiúsculas/minúsculas normais o que veio todo em maiúsculas (como a Receita manda). */
export function smartCase(value: string): string {
  return /\p{Ll}/u.test(value) ? value.trim() : titleCase(value);
}

/** "FABRICAÇÃO DE PRODUTOS DE PADARIA" → "Fabricação de produtos de padaria". */
export function sentenceCase(value: string): string {
  const lower = value.toLowerCase().replace(/\s+/g, ' ').trim();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Nome da empresa, o mesmo em todas as abas: nome fantasia, nome do site, razão social; sem
 * nenhum deles, o domínio ou o termo pesquisado.
 */
export function companyName(data: ExplorerData, fallback: string): string {
  const name =
    asText(data.company?.nickname) ??
    asText(data.name) ??
    asText(data.site?.name) ??
    asText(data.company?.name);
  return name ? smartCase(name) : fallback;
}

/** Favicon do site (serviço do Google; ícone menor que 32 px = o site não tem). */
export function faviconUrl(domain: string | undefined): string | null {
  return domain
    ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`
    : null;
}

/** CNPJ da análise: o do pedido, o da Receita (número no banco) ou nenhum. */
export function cnpjOf(data: ExplorerData, mode: ExplorerMode, term: string): string {
  const raw =
    asText(data.cnpj) ??
    asText((data.company as { cnpj?: unknown } | undefined)?.cnpj) ??
    (mode === 'cnpj' ? term : '');
  const digits = raw.replace(/\D/g, '');
  return digits ? cnpjMask(digits.padStart(14, '0')) : '';
}

/** Data "AAAA-MM-DD" vira data local (sem o fuso puxar para o dia anterior). */
export function toDate(value: Date | string): Date {
  if (value instanceof Date) return value;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
}

const SIZE: Record<string, [LxKey, LxKey]> = {
  'MICROEMPREENDEDOR INDIVIDUAL': ['size_mei', 'size_mei_rev'],
  'MICRO EMPRESA': ['size_micro', 'size_micro_rev'],
  'EMPRESA DE PEQUENO PORTE': ['size_small', 'size_small_rev'],
  DEMAIS: ['size_other', 'size_other_rev'],
};

/** Porte da Receita → nome curto e faixa de faturamento. */
export function companySize(porte: string | undefined): [LxKey, LxKey] | null {
  return porte ? (SIZE[porte.toUpperCase()] ?? null) : null;
}

/** 11222333000181 → 11.222.333/0001-81. */
export function cnpjMask(value: string | undefined): string {
  const d = String(value ?? '').replace(/\D/g, '');
  return d.length === 14
    ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
    : String(value ?? '');
}

/*
 * Os campos do site no banco são de formato livre (adtargeting, statscrop, similarweb gravados
 * como vieram). Antes de desenhar, lista vira lista, número vira número e texto vira texto;
 * o que não tiver o formato esperado some, em vez de derrubar a tela.
 */

/** Lista de verdade; num objeto, a primeira lista que ele guardar (ex.: { list: [...] }). */
export function asArray<T = Record<string, unknown>>(value: unknown): T[] {
  if (Array.isArray(value)) return value.filter((item) => item != null) as T[];
  if (value && typeof value === 'object') {
    const inner = Object.values(value as Record<string, unknown>).find(Array.isArray);
    if (inner) return (inner as unknown[]).filter((item) => item != null) as T[];
  }
  return [];
}

/** Número finito ("2,5", "2.5" e 2.5 valem); o resto vira null. */
export function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.replace('%', '').replace(',', '.').trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Texto para mostrar (número vira texto); objeto, lista e vazio viram null. */
export function asText(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

/* ---------- LinkedIn (base do Snout) ---------- */

/** País como vem do LinkedIn (inglês, minúsculo) → código, para o nome no idioma da pessoa. */
const LINKEDIN_COUNTRY: Record<string, string> = {
  brazil: 'BR',
  portugal: 'PT',
  argentina: 'AR',
  chile: 'CL',
  colombia: 'CO',
  mexico: 'MX',
  uruguay: 'UY',
  paraguay: 'PY',
  peru: 'PE',
  'united states': 'US',
  canada: 'CA',
  spain: 'ES',
  france: 'FR',
  italy: 'IT',
  germany: 'DE',
  'united kingdom': 'GB',
};

function linkedinCountry(name: string, locale: string): string {
  const code = LINKEDIN_COUNTRY[name.toLowerCase()];
  if (code) {
    try {
      const localized = new Intl.DisplayNames([locale], { type: 'region' }).of(code);
      if (localized) return localized;
    } catch {
      // sem Intl.DisplayNames: fica o nome em inglês
    }
  }
  return titleCase(name);
}

export interface LinkedinFacts {
  url: string;
  name: string;
  /** "company/nubank" */
  handle: string;
  industry: string;
  people: string;
  founded: number | null;
  place: string;
  website: string;
}

/** O que a aba LinkedIn e o PDF mostram, já no idioma da pessoa (o setor pela tabela traduzida). */
export function linkedinFacts(
  li: ExplorerLinkedin | undefined,
  tx: (key: LxKey, vars?: Record<string, string | number>) => string,
  locale: string,
): LinkedinFacts | null {
  const url = asText(li?.url);
  if (!li || !url) return null;
  const num = (n: number): string => new Intl.NumberFormat(locale).format(n);
  const size = asText(li.size) ?? '';
  const plus = /^(\d+)\+$/.exec(size);
  const range = /^(\d+)-(\d+)$/.exec(size);
  const people = plus
    ? tx('li_size_plus', { n: num(Number(plus[1]) - 1) })
    : range
      ? tx('li_size_range', { from: num(Number(range[1])), to: num(Number(range[2])) })
      : size;
  const country = asText(li.country);
  const lang = locale.slice(0, 2).toLowerCase() as keyof NonNullable<
    ExplorerLinkedin['industryNames']
  >;
  const translated = asText(li.industryNames?.[lang]) ?? asText(li.industryNames?.en);
  return {
    url,
    name: asText(li.name) ? titleCase(asText(li.name)!) : 'LinkedIn',
    handle: url.replace(/^https?:\/\/([a-z]{2,3}\.)?linkedin\.com\//i, '').replace(/\/$/, ''),
    industry: translated ?? (asText(li.industry) ? sentenceCase(asText(li.industry)!) : ''),
    people,
    founded: typeof li.founded === 'number' && li.founded > 1000 ? li.founded : null,
    place: [
      ...[li.locality, li.region]
        .map((part) => asText(part))
        .filter((part): part is string => Boolean(part))
        .map((part) => titleCase(part)),
      ...(country ? [linkedinCountry(country, locale)] : []),
    ].join(', '),
    website: asText(li.website) ?? '',
  };
}

/** "pt-BR" → "Português (Brasil)", no idioma da pessoa; sem como traduzir, o código. */
export function languageName(code: string | undefined, locale: string): string {
  const tag = asText(code)?.replace('_', '-');
  if (!tag) return '';
  try {
    const name = new Intl.DisplayNames([locale], { type: 'language' }).of(tag);
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : tag;
  } catch {
    return tag;
  }
}

/** Texto da IA pode vir com marcação de Markdown leve: fica só o texto. */
export function plainText(value: unknown): string {
  const text = Array.isArray(value)
    ? value
        .map((item) => asText(item) ?? '')
        .filter(Boolean)
        .join(', ')
    : value && typeof value === 'object'
      ? Object.values(value as Record<string, unknown>)
          .map((item) => asText(item) ?? '')
          .filter(Boolean)
          .join(', ')
      : (asText(value) ?? '');
  return text
    .replace(/<[^>]+>/g, ' ')
    .replace(/[*_`#]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** O que a pesquisa na internet trouxe (registro antigo, antes do dossiê único). */
export function researchBlocks(data: ExplorerData): {
  blocks: [LxKey, string][];
  mvv: [LxKey, string][];
  facts: [LxKey, string][];
} {
  const r = data.resume ?? {};
  const pick = (list: [LxKey, unknown][]): [LxKey, string][] =>
    list.map(([key, value]): [LxKey, string] => [key, plainText(value)]).filter(([, text]) => text);
  return {
    blocks: pick([
      ['pub_segment', r.market_segmentation],
      ['pub_products', r.products_and_services],
      ['pub_culture', r.company_culture],
      ['pub_target', r.target_market],
    ]),
    mvv: pick([
      ['pub_mission', r.mission],
      ['pub_vision', r.vision],
      ['pub_values', r.values],
    ]),
    facts: pick([
      ['pub_employees', r.number_of_employees],
      ['pub_revenue', r.annual_revenue],
      ['pub_news', r.recent_news],
    ]),
  };
}

/** O dossiê em texto para copiar: o documento e, no registro antigo, a pesquisa embaixo. */
export function dossierText(data: ExplorerData, tx: (key: LxKey) => string): string {
  const parts = [reportToText(parseReport(data.report))];
  if (data.dossier?.version !== 2) {
    const { blocks, mvv, facts } = researchBlocks(data);
    const rows = [...blocks, ...mvv, ...facts];
    if (rows.length) {
      parts.push('', tx('dossier_about'));
      rows.forEach(([key, text]) => parts.push('', tx(key), text));
    }
  }
  return parts.join('\n').trim();
}
